// ─────────────────────────────────────────────────────────────────────────
//  ClickViewer · web · API 호출
//
//  이 파일이 맡는 것: fetch 를 감싸는 통로 **하나**.
//  ★ 화면에서 `fetch()` 를 직접 부르지 마라. 아래 셋 중 하나라도 빠지면 조용히 틀린다.
//      ① credentials:'include'  — 안 붙이면 쿠키가 안 실려 **영원히 401**
//      ② content-type: json     — 안 붙이면 **415**(apps/api 의 CSRF 3중 방어 중 하나)
//      ③ 오류 본문 `{"e":"…"}` 해석 — 그대로 보여주면 사용자가 `not_found` 를 읽는다
//  ★ 그리고 **네트워크 실패와 401 을 반드시 구분**한다. 현장에서 신호가 나쁜 것과
//    로그아웃된 것은 사용자에게 완전히 다른 일이다(전자는 기다리면 되고, 후자는 다시 로그인).
//
//  ★★ [합쳤다 2026-08-11] `screens/admin/api.ts` 에 **두 번째 통로**가 있었다.
//     그 파일은 「타임아웃 하나 때문에」 따로 만들어졌는데, 갈라진 값이 타임아웃만이
//     아니었다 — 그쪽 `req` 는 401 을 받아도 `onUnauthorized()` 를 **안 불렀다.**
//     그 통로를 쓰는 화면이 관리 화면 다섯 · 업로드 · **공유링크 방문 화면**까지 퍼져
//     있어서, 세션이 끊기면 client.ts 를 타는 화면은 로그인으로 가는데 저쪽은
//     「로그인이 필요합니다」 글자만 띄우고 **그 자리에 머물렀다.**
//     아래 「판정은 한 군데여야 한다」가 정확히 그 자리다. 그래서 타임아웃을 이쪽으로
//     들여오고 `req` 도 여기서 낸다. `screens/admin/api.ts` 는 재수출만 남았다.
// ─────────────────────────────────────────────────────────────────────────

import { USE_MOCK } from '../config';
import { mockFetch } from './mock';

// ═══ 서버가 주는 모양 ══════════════════════════════════════════════════════
// 근거: apps/api/src/routes/auth.ts 의 getMe()

export interface User {
  id: string;
  email: string;
  name: string | null;
  is_superadmin: boolean;
}

export interface Project {
  id: string;
  slug: string;
  name: string;
  /** 좌표 원점대. `EPSG:5186`(중부원점, 당진) 같은 값이다. */
  belt: string;
  /**
   * `active` | `suspended` …
   *
   * ⚠️⚠️ [정정 2026-08-13] 여기 *「정지된 현장은 목록에 뜨되 들어갈 수 없다」*고 적혀
   *    있었다. **거짓이다.** 목록은 RLS 로만 걸러지는데(`routes/projects.ts` 의 `getProjects`
   *    는 where 절에 권한 조건을 안 쓴다), 그 정책 `project_select`(`db/004_rls.sql:93`)가
   *    `app.my_projects('project.view')` 를 쓰고 그 함수가 `pr.state = 'active'` 로 거른다
   *    (`db/003_functions.sql:94`). → **정지된 현장은 목록에 줄 자체가 안 온다.**
   *    그래서 `screens/Projects.tsx` 의 `StateBadge`·`AdminLinks` 의 suspended 분기는
   *    지금 **한 번도 안 도는 길**이다.
   *    ⓘ 지우지는 않았다. 정지 현장을 목록에 보여주기로 정하는 날 고칠 곳은 **화면이 아니라
   *      위 두 SQL** 이고, 그때 저 분기들이 그대로 필요하다.
   */
  state?: string;
  /** 휴지통에 든 것은 안 센다. */
  model_count?: number;
  /**
   * **이 현장에서 내가 할 수 있는 것 전부.** `['model.view', 'model.upload', …]`
   *
   * ★ 이 배열 하나에 **모듈 on/off + 역할 + 조직관리자 + superadmin 이 전부 반영**돼 있다.
   *   화면은 `perms.includes('model.upload')` 만 보면 된다.
   *
   * ⚠️ **역할 이름으로 판정하지 마라.** DB 는 "admin 은 프로젝트 역할이 아니다" 로 되어 있어
   *   `project_member` 행이 없는 조직 관리자·superadmin 이 존재한다. 역할로 판정하면
   *   **그 두 사람의 화면에서 버튼이 통째로 사라진다.**
   *
   * `/api/me` 로 받은 목록에는 이 값이 없다(권한은 현장별이라 거기 둘 자리가 없다).
   */
  perms?: string[];
}

/** `GET /api/me` 의 응답. */
export interface Me {
  user: User;
  projects: Project[];
}

// ═══ 오류 ═════════════════════════════════════════════════════════════════

/**
 * 화면이 **구분해서 다뤄야 하는** 실패의 종류.
 *
 * HTTP 상태 코드를 그대로 쓰지 않는 이유: 화면이 하는 판단은 "숫자가 몇이냐" 가 아니라
 * "다시 시도하면 되나 / 로그인해야 하나 / 사람에게 연락해야 하나" 셋뿐이다.
 */
export type ApiErrorKind =
  /** 요청이 서버에 닿지도 못했다. 신호 없음 · DNS · **API 를 안 켰음**. → 다시 시도 */
  | 'offline'
  /** 401. 미로그인 · 세션 만료 · 코드 불일치가 **전부 이것**이다(서버가 구분해 주지 않는다) */
  | 'unauthorized'
  /** 403. 지금은 CSRF(Origin 불일치)뿐이다. dev 에서는 거의 프록시 설정 문제다 */
  | 'forbidden'
  /** 404. ⚠️ 「없다」가 아니라 **「너에게는 없다」** 일 수 있다. notFoundIsMaybePermission 참고 */
  | 'not_found'
  /** 400 · 415. 우리가 잘못 보냈다 = 화면의 버그 */
  | 'bad_request'
  /** 503. 서버 잘못이고 **곧 나을 수 있다**. → 다시 시도 */
  | 'unavailable'
  /** 500 등. 서버 잘못이고 **저절로 안 낫는다**. → 연락 */
  | 'server'
  /** 응답이 JSON 이 아니다. 프록시가 HTML 오류 페이지를 끼워 넣었거나 서버가 반쯤 죽었다 */
  | 'malformed';

export class ApiError extends Error {
  readonly kind: ApiErrorKind;
  /** HTTP 상태. 네트워크 실패면 0. */
  readonly status: number;
  /** 서버가 준 `{"e":"…"}` 의 값. 없으면 null. **사람에게 보여주지 않는다.** */
  readonly code: string | null;
  /**
   * 응답 헤더의 `x-request-id`.
   * ⚠️ **404 에는 일부러 없다**(apps/api/src/errors.ts). 헤더 개수 차이만으로도
   *    "진짜 404" 와 "막힌 404" 를 구분할 수 있기 때문이다. 없다고 버그가 아니다.
   */
  readonly requestId: string | null;

  constructor(kind: ApiErrorKind, status: number, code: string | null, requestId: string | null) {
    super(`api ${kind} (${status}${code ? ` ${code}` : ''})`);
    this.name = 'ApiError';
    this.kind = kind;
    this.status = status;
    this.code = code;
    this.requestId = requestId;
  }
}

/**
 * `ApiError` 에 **서버가 준 나머지 필드**를 얹은 것.
 *
 * `bad_module_combo` 의 `{module, needs}`, `bad_email` 의 `{bad:[…]}` 를 화면이 문장으로 만든다.
 * 그것 없이 `humanMessage()` 만 쓰면 "요청 형식이 잘못되었습니다" 가 되어 고칠 수가 없다.
 *
 * ⚠️ **`req()`(= `keepBody`)로 부른 요청만** 이것을 받는다. 아래 `request()` 의 기본
 *    경로는 `e` 하나만 꺼내고 나머지를 버린다 — 로그인·세션 응답에는 읽을 나머지가 없고,
 *    본문을 통째로 들고 다니면 그 값이 로그·오류 리포트로 새기 때문이다.
 */
export class AdminError extends ApiError {
  readonly extra: Record<string, unknown>;
  constructor(
    kind: ApiErrorKind,
    status: number,
    code: string | null,
    requestId: string | null,
    extra: Record<string, unknown>,
  ) {
    super(kind, status, code, requestId);
    this.extra = extra;
  }
}

/**
 * 오류를 **우리말 한 문장**으로.
 *
 * ★ 서버가 준 `e` 값을 그대로 보여주지 않는다. `not_found` 를 읽은 사용자는
 *   자기가 뭘 잘못했는지 알 수 없고, 우리에게 연락할 말도 못 찾는다.
 * ★ 그리고 **다음에 무엇을 하면 되는지**를 문장 안에 넣는다. 상태만 알려주는 오류는
 *   "그래서 어쩌라고" 로 끝난다.
 */
export function humanMessage(err: unknown): string {
  if (!(err instanceof ApiError)) {
    return '알 수 없는 문제가 생겼습니다. 새로고침해 보세요.';
  }
  switch (err.kind) {
    case 'offline':
      // dev 에서 압도적으로 흔한 원인이 "API 를 안 켰다" 라서 따로 짚어 준다.
      return err.code === 'api_unreachable'
        ? '서버(API)가 떠 있지 않습니다. apps/api 에서 npm run dev 를 먼저 실행하세요.'
        : '서버에 연결하지 못했습니다. 신호를 확인하고 다시 시도해 주세요.';
    case 'unauthorized':
      return '로그인이 필요합니다. 다시 로그인해 주세요.';
    case 'forbidden':
      return err.code === 'bad_origin'
        ? '요청이 거부되었습니다(Origin 불일치). dev 라면 vite.config.ts 의 프록시 설정을 보세요.'
        : '요청이 거부되었습니다.';
    case 'not_found':
      // ⚠️ "없습니다" 라고 **단정하지 않는다.** 아래 notFoundIsMaybePermission 주석 참고.
      return '요청하신 것을 찾을 수 없습니다. 주소가 맞는지, 권한이 있는지 확인해 주세요.';
    case 'bad_request':
      return '요청 형식이 잘못되었습니다. 새로고침 후 다시 시도해 주세요.';
    case 'unavailable':
      return '서버가 잠시 응답하지 못하고 있습니다. 잠시 뒤 다시 시도해 주세요.';
    case 'server':
      return '서버에서 문제가 생겼습니다. 계속되면 담당자에게 알려 주세요.';
    case 'malformed':
      return '서버 응답을 이해하지 못했습니다. 계속되면 담당자에게 알려 주세요.';
  }
}

/**
 * ★★★ 이 프로젝트에서 제일 자주 오해되는 규칙 ★★★
 *
 * **404 는 「없음」이 아니라 「권한 없음」일 수 있다.**
 *
 * 설계상 비멤버에게는 403 이 아니라 **404** 를 준다(DB-DESIGN §7-5).
 * 403 을 주면 그 응답 자체가 "그 현장은 존재한다" 는 확인이 되기 때문이다.
 * 그래서 서버 입장에서 아래 넷은 **바이트 단위로 같은 응답**이다.
 *
 *   · 없는 현장   · 내 현장이 아닌 현장   · 삭제된 현장   · 없는 주소
 *
 * → 화면이 "그런 현장은 없습니다" 라고 **단정하면 거짓말이 된다.** 실제로는 초대만
 *   받으면 되는 사람에게 "없다" 고 말해서 돌려보내는 것이다.
 * → 문구는 항상 **「볼 수 있는 것이 없다」 + 「담당자에게 요청하라」 + 연락처** 로 쓴다.
 */
export const notFoundIsMaybePermission = true;

// ═══ 401 을 앱 전체에 알리는 통로 ═════════════════════════════════════════

let onUnauthorized: (() => void) | null = null;

/**
 * 세션이 끊긴 것을 알아채는 자리를 **한 곳**으로 모은다(SessionProvider 가 등록한다).
 *
 * 왜 필요한가: 다른 탭에서 로그아웃했거나 30일이 지나면, 멀쩡히 보고 있던 화면의
 * 다음 요청이 갑자기 401 이 된다. 각 화면이 알아서 처리하게 두면 어떤 화면은
 * 로그인으로 보내고 어떤 화면은 "오류" 만 띄운다. 판정은 한 군데여야 한다.
 */
export function setUnauthorizedHandler(fn: (() => void) | null): void {
  onUnauthorized = fn;
}

// ═══ 요청 ═════════════════════════════════════════════════════════════════

/**
 * 요청 하나의 상한. 15초를 넘기면 사용자는 이미 다시 누를 준비를 하고 있다.
 *
 * ★★ 왜 있어야 하나(원래 `screens/admin/api.ts` 머리말에 있던 근거를 옮겨 왔다):
 *    현장 LTE 음영에서 「현장 만들기」를 누르면 응답이 영영 안 온다. 소장은 다시 누른다.
 *    첫 요청은 이미 서버에 닿아 **커밋됐다.** 두 번째 요청이 23505 → 409 `slug_taken`
 *    을 받고, 화면은 이렇게 말한다:
 *       「dangjin」 은 이미 쓰이고 있습니다. 지운 현장이 쓰던 주소일 수도 있습니다.
 *    **본인이 3초 전에 성공적으로 만든 현장을 두고 이 문장을 읽는다.** 이 제품에서
 *    가능한 최악의 오해다. 그는 `dangjin-2` 로 다시 만들고, 둘 다 못 지운다(삭제 라우트 없음).
 *
 * ⚠️ 타이머는 **응답 헤더가 온 시점에** 끈다(아래 `finally`). 본문 읽기까지 덮으면
 *    큰 목록을 받는 중에 끊긴다.
 */
const TIMEOUT_MS = 15_000;

interface RequestOptions {
  /**
   * 401 을 받아도 전역 로그아웃 처리를 **하지 않는다.**
   *
   * 로그인 화면에서만 쓴다. 거기서 401 은 "세션이 끊겼다" 가 아니라
   * **"코드가 틀렸다"** 이기 때문이다. 구분하지 않으면 코드를 한 번 잘못 친 사람이
   * 이메일 입력 단계로 튕겨 나가고, 방금 받은 코드를 다시 못 쓴다.
   */
  silent401?: boolean;
  /**
   * 오류 본문을 **통째로** 보존해 `AdminError` 로 던진다(`req()` 가 켠다).
   *
   * 켜지 않으면 `e` 하나만 남는다. 관리 화면은 `{module, needs}` · `{bad:[…]}` 같은
   * 나머지 칸으로 문장을 만들기 때문에 그것이 없으면 사용자가 **무엇을 고쳐야 하는지**를
   * 알 수 없다.
   */
  keepBody?: boolean;
}

async function request<T>(
  method: 'GET' | 'POST',
  path: string,
  body?: unknown,
  opts: RequestOptions = {},
): Promise<T> {
  const headers: Record<string, string> = { accept: 'application/json' };

  if (method === 'POST') {
    // ★ 본문이 없어도 붙인다. apps/api 는 라우트별로 CT 를 강제하는데(Route.json),
    //   지금 안 걸린 라우트가 나중에 걸리는 쪽으로 바뀌면 **그날 조용히 415** 가 된다.
    //   한 규칙으로 두면 그 사고가 애초에 안 난다.
    headers['content-type'] = 'application/json';
  }

  const ac = new AbortController();
  const timer = window.setTimeout(() => ac.abort(), TIMEOUT_MS);

  let res: Response;
  try {
    const doFetch = USE_MOCK ? mockFetch : fetch;
    res = await doFetch(path, {
      method,
      headers,
      // ★★★ 이게 빠지면 쿠키가 안 실린다. 세션은 HttpOnly 쿠키에만 있으므로
      //     모든 요청이 401 이 되고, 로그인은 "성공했는데 안 된" 상태가 된다.
      credentials: 'include',
      // 로그인·세션 응답이 중간 캐시에 남으면 그 자체가 사고다. 서버도 no-store 를 주지만
      // 브라우저 캐시(bfcache 포함)를 한 겹 더 막는다.
      cache: 'no-store',
      signal: ac.signal,
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
  } catch {
    // fetch 가 **던지는** 경우 = 응답이 아예 없다. 서버가 안 떴거나 신호가 끊겼다.
    // ⚠️ CORS 차단도 여기로 온다. 그 경우 원인은 프록시 설정이지 네트워크가 아니다.
    // ⚠️ 타임아웃(abort)도 여기로 온다. 사용자에게는 둘 다 「연결이 안 됩니다」로 같다 —
    //    다른 것은 우리가 기다린 시간뿐이고, 다음에 할 일(다시 시도)은 똑같다.
    //    (취소를 구분해야 하는 곳은 `requestRaw` 다. 거기는 사용자가 [취소] 를 누른다.)
    throw new ApiError('offline', 0, null, null);
  } finally {
    window.clearTimeout(timer);
  }

  const requestId = res.headers.get('x-request-id');

  if (res.ok) {
    // 204 는 본문이 없다. `/api/auth/code` 와 `/api/auth/logout` 이 항상 이것이다.
    if (res.status === 204) return undefined as T;
    try {
      return (await res.json()) as T;
    } catch {
      throw new ApiError('malformed', res.status, null, requestId);
    }
  }

  // ─ 오류 본문에서 `e` 를 꺼낸다. 없어도 진행한다(프록시가 HTML 을 끼울 수 있다) ─
  let code: string | null = null;
  let extra: Record<string, unknown> = {};
  try {
    const parsed: unknown = await res.json();
    if (parsed && typeof parsed === 'object') {
      if (opts.keepBody) extra = parsed as Record<string, unknown>;
      if (typeof (parsed as { e?: unknown }).e === 'string') code = (parsed as { e: string }).e;
    }
  } catch {
    /* 본문이 JSON 이 아니다. 상태 코드만으로 판정한다. */
  }

  // dev 프록시가 만들어 주는 것(vite.config.ts). "API 를 안 켰다" 를 502 로 알려 준다.
  if (code === 'api_unreachable') throw new ApiError('offline', res.status, code, requestId);

  const kind: ApiErrorKind =
    res.status === 401 ? 'unauthorized'
    : res.status === 403 ? 'forbidden'
    : res.status === 404 ? 'not_found'
    // ⚠️ 409 → `not_found` 는 **관리 화면에서 옮겨 온 그대로**다. 뜻이 맞아서가 아니라,
    //    그 화면들이 이미 이 값 위에 서 있어서 바꾸면 조용히 다른 문구가 뜬다.
    //    409 를 받는 쪽은 `humanMessage()` 를 안 보고 `AdminError.extra` 를 읽는다 —
    //    거기 `{e:'bad_module_combo', module, needs}` 처럼 진짜 답이 들어 있다.
    //    (`request()` 만 쓰는 로그인·세션 라우트는 409 를 내지 않는다. 확인함)
    : res.status === 409 ? 'not_found'
    : res.status === 400 || res.status === 415 ? 'bad_request'
    // ⚠️ [2026-08-12] **429 를 `unavailable` 로 보낸다.** 「지금은 안 되지만 곧 된다」가
    //    이 kind 의 뜻이고, 429 가 정확히 그것이다(약 5분 뒤 다시 된다 — db/023).
    //    ★ 전에는 아래 `'server'` 로 떨어져 로그인 화면에 **「서버 오류」**가 떴다.
    //      사용자는 자기가 뭘 잘못했는지 모른 채 「고장 났나」로 읽고, 실제로는
    //      **기다리면 되는 상황**이다. 새 kind 를 만들지 않은 이유는 `humanMessage` 와
    //      `ErrorState` 가 이미 `unavailable` 을 「잠시 뒤 다시」로 그리기 때문이다.
    //    ⚠️ 더 정확한 문장(몇 분 뒤인지)이 필요한 화면은 `e.status === 429` 로 직접 갈라라 —
    //      `screens/ShareVisitor.tsx` 가 그렇게 한다(그 화면은 「기다리면 된다」와
    //      「링크가 죽었다」를 반드시 갈라야 해서 kind 만으로는 부족하다).
    : res.status === 429 ? 'unavailable'
    : res.status === 503 || res.status === 502 ? 'unavailable'
    : 'server';

  if (kind === 'unauthorized' && !opts.silent401) onUnauthorized?.();

  throw opts.keepBody
    ? new AdminError(kind, res.status, code, requestId, extra)
    : new ApiError(kind, res.status, code, requestId);
}

/**
 * 오류 본문을 통째로 들고 오는 통로. **`request()` 와 같은 몸통을 쓴다.**
 *
 * ★ 관리 화면 · 업로드 · 공유링크 방문 화면이 전부 이것을 부른다. 그래서 401 이
 *   여기서도 **반드시** `onUnauthorized()` 를 타야 한다 — 안 그러면 세션이 끊긴
 *   사용자가 「로그인이 필요합니다」만 읽으며 그 화면에 갇힌다(머리말의 그 사고).
 *
 * ⚠️ **공유링크 방문 화면(`ShareVisitor`)도 이것을 쓴다.** 그 사람은 로그인한 적이
 *   없으므로 401 이 오면 로그인 화면으로 튕겨서는 안 된다. 지금 안전한 이유는
 *   「로그인 뒤 화면이라서」가 **아니라** — 거기가 부르는 `POST /api/s/open` 이
 *   **401 을 아예 안 내기 때문**이다(`apps/api/src/routes/share.ts` 에 401 이 0건,
 *   400·200 만 낸다. `index.ts:153` 의 `/api/s/` 갈래에도 인증 관문이 없다).
 *   ★ share 라우트에 401 을 새로 만들 일이 생기면 **여기 `silent401` 을 열어라.**
 */
export function req<T>(method: 'GET' | 'POST', path: string, body?: unknown): Promise<T> {
  return request<T>(method, path, body, { keepBody: true });
}

// ═══ 화면이 부르는 것 ═════════════════════════════════════════════════════

/**
 * 로그인 코드 요청. **항상 204 다.**
 *
 * ⚠️ 성공했다고 코드가 간 것이 아니다. 아래 전부가 204 로 똑같이 보인다.
 *    · 없는 이메일        (가입 여부를 숨기려고 일부러 그렇게 만들었다)
 *    · 분당 1회 제한에 걸림
 *    · DB 가 죽어 있음    (apps/api 가 응답을 먼저 보내고 뒤에서 처리하기 때문)
 *    → 그래서 화면은 "보냈습니다" 가 아니라 **"보냈다면 곧 도착합니다"** 쪽으로 말하고,
 *      안 오는 사람을 위해 연락처를 항상 띄워 둔다.
 *
 * 400 이 오는 경우는 **이메일 형식**뿐이다. 형식은 서버에 묻지 않아도 알 수 있는 것이라
 * 400 을 줘도 가입 여부가 새지 않는다.
 * 503 이면 서버에 CV_CODE_PEPPER 가 없다는 뜻이다(apps/api README §4-4).
 */
export function requestLoginCode(email: string): Promise<void> {
  return request<void>('POST', '/api/auth/code', { email }, { silent401: true });
}

/**
 * 코드 확인 → 세션 쿠키 발급.
 *
 * ⚠️ 401 의 사유를 서버가 **구분해 주지 않는다.** 틀린 코드 · 만료된 코드 · 이미 쓴 코드가
 *    전부 401 이다. 그러니 화면도 사유를 지어내지 말고 셋을 한 문장에 담는다.
 * ⚠️ [2026-08-12 · db/023] **「5회 초과」는 없어졌다.** 코드가 죽는 길은 이제 **만료(10분)와
 *    한 번 씀** 둘뿐이다 — 몇 번을 틀려도 그 코드는 살아 있다.
 *    대신 **너무 많이 틀리면 429**(`too_many`)가 온다. 그건 401 이 아니라 **다른 코드**라
 *    화면이 갈라서 말할 수 있다: 「잠시 뒤(약 5분) 다시」. 401 문장에 그것을 섞지 마라.
 * ⚠️ 성공 응답의 `Set-Cookie` 는 JS 가 읽을 수 없다(HttpOnly). 그래서 여기서
 *    "로그인됨" 을 여기서 판단하지 않고, `/api/me` 를 **다시 불러서** 판단한다(SessionProvider).
 */
export function verifyLoginCode(email: string, code: string): Promise<{ ok: true }> {
  return request<{ ok: true }>('POST', '/api/auth/verify', { email, code }, { silent401: true });
}

/**
 * 나와 내 현장.
 *
 * ★ **이것이 로그인 상태의 유일한 진실 원천이다.** localStorage 에 아무것도 저장하지 않는다 —
 *   세션은 HttpOnly 쿠키에 있고 JS 는 그걸 못 읽는다. 따로 저장하면 그 사본이 반드시
 *   진실과 어긋나고, 그때 화면은 "로그인된 것처럼 보이는데 전부 401" 이 된다.
 */
export function getMe(): Promise<Me> {
  return request<Me>('GET', '/api/me');
}

/**
 * 내 현장 목록.
 *
 * `[2026-08-06]` `/api/me` 에서 `/api/projects` 로 옮겼다.
 * `/api/me` 도 목록을 주지만 **`perms` 가 없다** — 권한은 현장별이라 거기 둘 자리가 없다.
 * 버튼을 보일지 말지 정하려면 이쪽이 필요하다.
 *
 * ⚠️ 404 가 오면 「없음」이 아니라 **「볼 수 있는 것이 없음」**일 수 있다.
 *   설계상 비멤버에게는 403 이 아니라 404 를 준다(존재 자체를 숨긴다).
 *   화면 문구를 "없습니다" 로 단정하지 마라 — `Projects.tsx` 의 빈 목록 문구 참고.
 */
export function getProjects(): Promise<Project[]> {
  return request<{ projects: Project[] }>('GET', '/api/projects').then((r) => r.projects);
}

/**
 * 로그아웃. **항상 204** 다(쿠키가 없어도, 이미 죽은 세션이어도).
 *
 * ★ 서버가 DB 의 `session.revoked_at` 을 채우므로 **즉시** 끊긴다. 만료를 기다리지 않는다.
 * ⚠️ 실패하더라도 화면은 로그아웃된 것처럼 굴어야 한다. 쿠키를 못 지우는 상황에서
 *    "로그아웃 실패" 를 띄우고 화면에 남겨 두면, 공용 PC 에서 그대로 자리를 뜬다.
 */
export function logout(): Promise<void> {
  return request<void>('POST', '/api/auth/logout', undefined, { silent401: true });
}

// ═══ 업로드용 — **응답 객체를 그대로 돌려주는** 통로 ══════════════════════
//
//  ⚠️ 아래는 **추가**다. 위의 함수를 한 줄도 고치지 않았다.
//
//  ★★ 왜 `request()` 로 안 되나 ★★
//
//   업로드는 **오류 본문의 나머지 칸을 화면이 읽어야 한다.** `request()` 는 `e` 하나만
//   꺼내고 나머지를 버리는데, 업로드에서는 그 버려지는 것들이 전부 「사용자가 다음에
//   무엇을 할 수 있는가」다:
//     · 413 `too_large`         → `max_bytes` 없이는 「몇 GB 까지 되는지」를 못 쓴다
//     · 409 `upload_in_progress`→ `upload_id`·`done[]` 없이는 **이어올리기가 불가능**하다
//     · 409 `model_name_taken`  → `model_id` 없이는 덮어쓰기 버튼을 만들 수 없다
//     · 409 `parts_missing`     → `missing[]` 없이는 자기 치유 경로가 없다
//   그리고 `request()` 는 메서드가 `GET|POST` 뿐이라 조각 PUT 도 취소 DELETE 도 못 한다.
//
//  ★★ 그렇다고 화면에서 `fetch()` 를 부르면 안 되는 이유 ★★
//
//   그 순간 **인증 경로가 두 벌**이 된다. 특히 조각 PUT 이 401 을 전역 통로로 안 보내면,
//   세션이 끊긴 채 30조각을 13분 동안 계속 밀다가 전부 실패하고 **사용자는 이유를
//   끝내 못 듣는다.** 그래서 아래 넷을 여기 한 곳에서 지킨다:
//     ① credentials:'include'   ② fetch throw → ApiError('offline')
//     ③ 401 → onUnauthorized() 호출 **후** throw   ④ 그 밖의 !ok → Response 그대로 반환

/** `requestRaw` 에 넘길 것. `body` 와 `json` 은 **동시에 주지 마라.** */
export interface RawInit {
  /**
   * 그대로 실어 보낼 본문(조각 Blob).
   * ⚠️ **content-type 을 붙이지 않는다.** 조각 PUT 라우트는 `json:false` 이고,
   *    붙이면 다른 검사에 걸릴 여지만 생긴다. `Content-Length` 는 브라우저가 Blob 에서
   *    자동으로 넣는다 — 손으로 넣을 수 없고 넣을 필요도 없다.
   */
  body?: BodyInit;
  /** JSON 본문. 주면 `content-type: application/json` 을 붙인다(라우트가 `json:true`). */
  json?: unknown;
  /** 취소용. **abort 는 오류가 아니다** — 아래 주석 참고. */
  signal?: AbortSignal;
}

/**
 * 응답을 **그대로** 돌려준다. 상태 코드 해석은 부르는 쪽이 한다.
 *
 * ⚠️ `!res.ok` 여도 **던지지 않는다**(401 만 예외). 던지면 위에 적은 칸들을 읽을 수 없다.
 *    → 부르는 쪽은 반드시 `res.ok` 를 보고 분기해라. 안 보면 오류 본문을 성공으로 읽는다.
 *
 * ⚠️ **AbortError 는 ApiError 로 바꾸지 않는다.** 사용자가 [취소] 를 누른 것과 신호가
 *    끊긴 것은 화면이 완전히 다르게 다뤄야 한다(전자는 정상 종료, 후자는 자동 재시도).
 *    뭉개면 [취소] 를 누른 사람에게 "연결이 끊겼습니다 — 다시 시도 중" 이 뜬다.
 */
export async function requestRaw(
  method: 'GET' | 'PUT' | 'POST' | 'DELETE',
  path: string,
  init: RawInit = {},
): Promise<Response> {
  const headers: Record<string, string> = { accept: 'application/json' };
  let body: BodyInit | undefined = init.body;
  if (init.json !== undefined) {
    headers['content-type'] = 'application/json';
    body = JSON.stringify(init.json);
  }

  let res: Response;
  try {
    res = await fetch(path, {
      method,
      headers,
      credentials: 'include', // ★★ 빠지면 쿠키가 안 실려 **영원히 401**
      cache: 'no-store',
      ...(body === undefined ? {} : { body }),
      ...(init.signal ? { signal: init.signal } : {}),
    });
  } catch (e) {
    // 취소는 그대로 던진다(위 주석). 그 밖은 전부 「서버에 닿지도 못했다」다.
    if (init.signal?.aborted || (e as { name?: string })?.name === 'AbortError') throw e;
    throw new ApiError('offline', 0, null, null);
  }

  if (res.status === 401) {
    // ★ 401 만 여기서 삼킨다 — 전역 로그아웃 통로를 반드시 타야 하기 때문이다.
    //   이 한 줄이 없으면 조각 PUT 경로가 세션 만료를 혼자만 알고 조용히 실패한다.
    onUnauthorized?.();
    throw new ApiError('unauthorized', 401, 'unauthorized', res.headers.get('x-request-id'));
  }
  return res;
}

/**
 * 오류 응답의 본문을 **통째로** 읽는다. 파싱 실패는 빈 객체다.
 *
 * ⚠️ Response 는 본문을 **한 번만** 읽을 수 있다. 한 응답에 두 번 부르지 마라 —
 *    두 번째는 조용히 `{}` 가 되고, 그러면 `missing[]` 이 사라져 자기 치유가 멈춘다.
 */
export async function readBody(res: Response): Promise<Record<string, unknown>> {
  try {
    const v: unknown = await res.json();
    return v && typeof v === 'object' ? (v as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}
